import { useState, useEffect, useCallback, useRef } from 'react';
import localforage from 'localforage';

const useLocalForage = (key, initialValue = null, init = null, storeConfig = {}, autoload = true) => {
    // 创建或获取 localforage 实例
    const storeRef = useRef(null);

    if (!storeRef.current) {
        storeRef.current = localforage.createInstance({
            name: 'useLocalForage',
            ...storeConfig
        });
    }

    const [value, setValue] = useState(initialValue);
    const [loading, setLoading] = useState();
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const keyRef = useRef(key);

    // 更新 key ref
    useEffect(() => {
        keyRef.current = key;
    }, [key]);

    /**
     * 从存储中读取值
     */
    const loadValue = useCallback(async () => {
        if (!keyRef.current) {
            setError(new Error('Key is required'));
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let storedValue = await storeRef.current.getItem(keyRef.current);
            if (storedValue) {
                if (init && typeof init === 'function') {
                    storedValue = init(storedValue);
                }
            } else {
                storedValue = initialValue;
            }
            setValue(storedValue);
            setLoaded(true);
            await storeRef.current.setItem(keyRef.current, storedValue);
            return storedValue;
        } catch (err) {
            const errorObj = err instanceof Error ? err : new Error(String(err));
            setError(errorObj);
            console.error('Error loading from localforage:', errorObj);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * 设置值到存储
     * @param {any} newValue - 要设置的值，可以是值或函数（函数接收当前值并返回新值）
     */
    const setStoredValue = useCallback(
        async (newValue) => {
            if (!keyRef.current) {
                setError(new Error('Key is required'));
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // 如果 newValue 是函数，则使用当前值调用它
                const valueToStore = typeof newValue === 'function' ? newValue(value) : newValue;
                setValue(valueToStore);
                await storeRef.current.setItem(keyRef.current, valueToStore);
            } catch (err) {
                const errorObj = err instanceof Error ? err : new Error(String(err));
                setError(errorObj);
                console.error('Error setting to localforage:', errorObj);
                throw errorObj;
            } finally {
                setLoading(false);
            }
        },
        [value]
    );

    /**
     * 从存储中删除值
     */
    const removeValue = useCallback(async () => {
        if (!keyRef.current) {
            setError(new Error('Key is required'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            setValue(null);
            await storeRef.current.removeItem(keyRef.current);
        } catch (err) {
            const errorObj = err instanceof Error ? err : new Error(String(err));
            setError(errorObj);
            console.error('Error removing from localforage:', errorObj);
            throw errorObj;
        } finally {
            setLoading(false);
        }
    }, []);

    // 自动加载数据
    useEffect(() => {
        if (autoload) loadValue();
    }, [autoload, loadValue]);

    return {
        value,
        loading,
        error,
        setValue: setStoredValue,
        removeValue,
        loaded,
        loadValue
    };
};

export default useLocalForage;
